//================================================================
//  Component: SideNav
//================================================================

//  Purpose: Contains the side menu 

//  Example:
//    <SideNav></SideNav>

//================================================================


//Libraries
import React, { useEffect, useContext, useReducer } from 'react';
import { Link, useLocation } from 'react-router-dom';

//Functions
import QueryDocument from '../../Library/QueryDocument';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';


export default function SideNav() {

    //------------------------------------------------------
    //  React Router & useContext
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const location = useLocation();

    // Check if user is a demand management admin
    const approverAdmin = getUser?.roles?.demandManagementAdmin === true || getUser?.roles?.commercialAdmin === true;

    //------------------------------------------------------
    //  Define Constants/Variables
    //------------------------------------------------------

    const menu = {
        'Requests': [
            {
                'title': 'View My Requests',
                'link': '/requests'
            }
        ],
        'Approvals': [
            {
                'title': 'View My Approvals',
                'link': '/approvals'
            },
        ]
    };

    // Add Admin menu only if the user is an admin
    if (approverAdmin) {
        menu['Admin'] = [
            { 'title': 'Approvers', 'link': '/admin/approvers' }
        ];
    }

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store number of requests
    const [taskCount, setTaskCount] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'requests': '',
            'approvals': '',
        }
    );

    //------------------------------------------------------
    //  Fuctions
    //------------------------------------------------------

    // Gets total count for selected nav items
    function getCount(item) {
        if (item.title === 'View My Requests') {
            return taskCount.requests
        }

        if (item.title === 'View My Approvals') {
            return taskCount.approvals
        }
    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Get all requests
    useEffect(() => {

        // Get number of pending requests for 'Requests' and 'Approvals' tabs
        const queries = [

            // Get total requests
            [
                ['createdby.email', '==', getUser.emailaddress],
                ['status', 'in', ['In Progress', 'Not Started']],
                ['tasktype', 'in', ['Digital Request', 'Digital Commercial Request']]
            ],

            // Get total approvals
            [
                ['assignedto.email', '==', getUser.emailaddress],
                ['status', 'in', ['In Progress', 'For Review']],
                ['tasktype', 'in', ['Initial Approval', 'Commercial Second Round Approval', 'Other Approval', 'Finance Approval', 'LoA Approval', 'COO Approval', 'Final Approval', 'Quote Review', 'Budget Owner (LoA) Approval', 'Finance Manager Approval']]
            ]

        ]

        const promises = [];

        queries.forEach((query) => {
            promises.push(
                QueryDocument('requests', query)
            )
        })

        // Settle promises
        Promise.all(promises)
            .then((result) => {

                // Filter our quote review requests from the Demand Management Portal
                const filteredApprovals = result[1].filter((request) => request?.requestorigin !== 'Demand');

                setTaskCount({
                    'requests': result[0].length,
                    'approvals': filteredApprovals.length,
                });

            })
            .catch((error) => {

                console.log('error', error);

            })

        // eslint-disable-next-line
    }, [getUser])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col gap-2 py-[25px] px-[30px] border-solid border-gray-200 border-1 h-full min-h-[calc(100vh-132.5px)] overflow-y-auto overflow-x-hidden'>

            {/* ----------------------------------------------- */}
            {/*    HEADER                                       */}
            {/* ----------------------------------------------- */}

            <h4 className='text-[22px] pb-2'>
                Account
            </h4>

            {/* ----------------------------------------------- */}
            {/*    BODY                                         */}
            {/* ----------------------------------------------- */}

            <div className='flex flex-col gap-3'>
                {
                    Object.entries(menu).map(([key, items], index) => (
                        <div className='w-full flex flex-col gap-2' key={index}>

                            {/* Link to parent page */}
                            <Link className='no-underline text-black px-3' to={items[0].link}>
                                <p className='font-semibold text-lg cursor-pointer m-0'>{key}</p>
                            </Link>

                            {/*  Link to child page */}
                            <div className='cursor-pointer'>
                                {
                                    items.map((item, i) => (
                                        <Link key={i} className={`no-underline text-black py-2 px-3 hover:bg-[#E3F1FB] whitespace-nowrap flex items-center text-base ${location?.pathname.includes(item?.link) ? 'bg-[#E3F1FB]' : 'bg-[#FFFFFF]'}`} to={item?.link}>

                                            {/* Title */}
                                            {item?.title}

                                            {/* Total Count */}
                                            <div className={`px-2 mx-2 text-[14px] rounded-[50%] ${location?.pathname.includes(item?.link) ? 'bg-[#FFFFFF]' : 'bg-[#E3F1FB]'}`}>
                                                {getCount(item)}
                                            </div>

                                        </Link>
                                    ))
                                }
                            </div>

                        </div>
                    ))
                }
            </div>

            {/* ----------------------------------------------- */}
            {/*    FOOTER                                       */}
            {/* ----------------------------------------------- */}

            <hr className='pl-3 border text-[#D8D8D8]'></hr>

            <div className='text-[#C4314B] pl-3 cursor-pointer' onClick={() => console.log(location)}>
                Sign Out
            </div>

        </div>
    )
}
